import { useEffect, useState } from 'react';
import { SignIn } from './components/SignIn';
import { UndoToast } from './components/UndoToast';
import { ensureDefaults } from './db/dexie';
import { useIsMobile } from './hooks/useData';
import { useSession } from './hooks/useSession';
import { useSync } from './hooks/useSync';
import { UndoProvider } from './hooks/useUndo';
import { DesktopShell } from './shells/DesktopShell';
import { MobileShell } from './shells/MobileShell';
import { sweepTrash } from './sync/purge';
import { isConfigured } from './sync/supabase';

/**
 * Two purpose-built shells over one set of components. Neither shell owns
 * feature logic, and mobile is not desktop at a narrower width — it does less.
 */
export default function App() {
  const isMobile = useIsMobile();
  const { userId, loading } = useSession();
  const sync = useSync(userId !== null);
  const [ready, setReady] = useState(false);

  // Every item belongs to exactly one list, so the Inbox has to exist before
  // anything reads or writes one.
  useEffect(() => {
    void ensureDefaults().finally(() => setReady(true));
  }, []);

  // Expired Trash, once per launch. Deliberately not part of the gate above:
  // it destroys data, so it must never be the thing standing between the user
  // and their notes, and it has nothing the first paint needs.
  useEffect(() => {
    if (!ready) return;
    void sweepTrash().then((purged) => {
      if (purged > 0) console.info(`[trash] purged ${purged} expired item(s)`);
    });
  }, [ready]);

  if (loading || !ready) return <div className="shell" />;
  if (isConfigured && !userId) return <SignIn />;

  return (
    <UndoProvider>
      {isMobile ? <MobileShell sync={sync} /> : <DesktopShell sync={sync} />}
      <UndoToast />
    </UndoProvider>
  );
}
